import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { launchImageLibrary, type PhotoQuality } from 'react-native-image-picker';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { KioskFaceCapture } from '../../components/KioskFaceCapture';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getFaceAttendanceConfig } from '../../config/faceAttendanceConfig';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { registerVisitor } from '../../services/visitorService';
import type { AttendanceCapture } from '../../types/faceAttendance';
import type { VisitorFaceImage } from '../../types/visitor';
import { showOperationFeedback } from '../../utils/operationFeedback';
import { deleteTemporaryImage } from '../../utils/temporaryImage';
import { visitorErrorMessage } from '../../utils/visitorErrors';
import {
  EMPTY_VISITOR_FORM,
  validatePickedImage,
  validateVisitorForm,
  type VisitorFormErrors,
  type VisitorFormValues,
} from '../../utils/visitorForm';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'VisitorRegister'>;

type TextField = Exclude<keyof VisitorFormValues, 'consentGiven' | 'faceImage'>;

export function VisitorRegisterScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [values, setValues] = useState<VisitorFormValues>(EMPTY_VISITOR_FORM);
  const [errors, setErrors] = useState<VisitorFormErrors>({});
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const faceUri = useRef<string | null>(null);

  const themed = useMemo(
    () => ({
      section: { ...typography.sectionTitle, color: palette.textPrimary },
      body: { ...typography.body, color: palette.textSecondary },
      error: { ...typography.caption, color: palette.error },
      checkbox: { borderColor: palette.border, backgroundColor: palette.surface },
      checkboxOn: { borderColor: palette.primary, backgroundColor: palette.primary },
      tick: { color: palette.onPrimary },
      preview: { borderColor: palette.border, backgroundColor: palette.background },
      banner: { backgroundColor: palette.surface, borderColor: palette.error },
    }),
    [palette],
  );

  // Captured / picked photos are temporary copies; never leave them behind.
  useEffect(
    () => () => {
      deleteTemporaryImage(faceUri.current).catch(() => undefined);
    },
    [],
  );

  const setField = useCallback((field: TextField, value: string) => {
    setValues(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: undefined }));
  }, []);

  const setFaceImage = useCallback((image: VisitorFaceImage | null) => {
    const previous = faceUri.current;
    faceUri.current = image?.uri ?? null;
    if (previous && previous !== image?.uri) {
      deleteTemporaryImage(previous).catch(() => undefined);
    }
    setValues(current => ({ ...current, faceImage: image }));
    setErrors(current => ({ ...current, faceImage: undefined }));
  }, []);

  const onCaptured = useCallback(
    (capture: AttendanceCapture) => {
      setCapturing(false);
      setFaceImage({
        uri: capture.uri,
        fileName: capture.fileName || `visitor-${Date.now()}.jpg`,
        mimeType: capture.mimeType,
        source: 'CAMERA',
      });
    },
    [setFaceImage],
  );

  const onCaptureError = useCallback((message: string) => {
    setCapturing(false);
    setErrors(current => ({
      ...current,
      faceImage: message || 'Unable to open the camera.',
    }));
  }, []);

  const onCaptureTimeout = useCallback(() => {
    setCapturing(false);
    setErrors(current => ({
      ...current,
      faceImage: 'No face was detected. Try again in good light.',
    }));
  }, []);

  const uploadFace = useCallback(async () => {
    const config = getFaceAttendanceConfig();
    const picked = await launchImageLibrary({
      mediaType: 'photo',
      selectionLimit: 1,
      includeBase64: false,
      quality: config.captureQuality as PhotoQuality,
      maxWidth: config.imageMaxWidth,
      maxHeight: config.imageMaxHeight,
      assetRepresentationMode: 'compatible',
    });
    if (picked.didCancel) return;
    const asset = picked.assets?.[0];
    if (picked.errorCode || !asset?.uri) {
      setErrors(current => ({ ...current, faceImage: 'Could not open that photo.' }));
      return;
    }
    const invalid = validatePickedImage({ type: asset.type, fileSize: asset.fileSize });
    if (invalid) {
      setErrors(current => ({ ...current, faceImage: invalid }));
      return;
    }
    setFaceImage({
      uri: asset.uri,
      fileName: asset.fileName || `visitor-${Date.now()}.jpg`,
      mimeType: asset.type || 'image/jpeg',
      source: 'UPLOAD',
    });
  }, [setFaceImage]);

  const submit = useCallback(async () => {
    if (submitting) return;
    const found = validateVisitorForm(values);
    setErrors(found);
    setSubmitError(null);
    if (Object.values(found).some(Boolean) || !values.faceImage) return;

    setSubmitting(true);
    try {
      const visitor = await registerVisitor({ ...values, faceImage: values.faceImage });
      showOperationFeedback('add', 'success', `${visitor.visitorName} is registered.`);
      setFaceImage(null);
      navigation.goBack();
    } catch (error) {
      const message = visitorErrorMessage(error, 'Unable to register the visitor.');
      setSubmitError(message);
      showOperationFeedback('add', 'failure', message);
    } finally {
      setSubmitting(false);
    }
  }, [navigation, setFaceImage, submitting, values]);

  if (capturing) {
    return (
      <View style={styles.cameraHost}>
        <KioskFaceCapture
          heading="Capture visitor face"
          onCaptured={onCaptured}
          onError={onCaptureError}
          onTimeout={onCaptureTimeout}
        />
        <AppButton
          title="Cancel"
          variant="secondary"
          onPress={() => setCapturing(false)}
          style={styles.cancelCapture}
        />
      </View>
    );
  }

  const field = (
    key: TextField,
    label: string,
    options: Partial<React.ComponentProps<typeof AppInput>> = {},
  ) => (
    <AppInput
      label={label}
      value={values[key]}
      onChangeText={value => setField(key, value)}
      error={errors[key]}
      editable={!submitting}
      {...options}
    />
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.VISITOR_MANAGE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen scroll keyboardSafe>
        <ScreenHeader title="Register visitor" onBack={navigation.goBack} />
        <View style={styles.form}>
          {submitError ? (
            <View style={[styles.banner, themed.banner]} accessibilityRole="alert">
              <Text style={themed.error}>{submitError}</Text>
            </View>
          ) : null}

          {field('visitorName', 'Visitor name *', {
            placeholder: 'Full name',
            autoCapitalize: 'words',
          })}
          {field('designation', 'Designation', { autoCapitalize: 'words' })}
          {field('companyName', 'Company name *', { autoCapitalize: 'words' })}
          {field('phoneNumber', 'Phone number *', {
            keyboardType: 'phone-pad',
            placeholder: '+91 98765 43210',
          })}
          {field('email', 'Email', {
            keyboardType: 'email-address',
            autoCapitalize: 'none',
            autoCorrect: false,
          })}
          {field('purposeOfVisit', 'Purpose of visit *', {
            placeholder: 'e.g. Interview, vendor meeting',
          })}

          <AppCard style={styles.faceCard}>
            <Text style={themed.section}>Visitor face *</Text>
            <Text style={themed.body}>
              Capture the face with the camera or upload a clear photo with exactly one face.
            </Text>
            {values.faceImage ? (
              <Image
                source={{ uri: values.faceImage.uri }}
                style={[styles.preview, themed.preview]}
                accessibilityLabel="Selected visitor face"
              />
            ) : null}
            <View style={styles.faceActions}>
              <AppButton
                title={values.faceImage?.source === 'CAMERA' ? 'Retake face' : 'Capture face'}
                onPress={() => setCapturing(true)}
                disabled={submitting}
                style={styles.faceAction}
              />
              <AppButton
                title="Upload face"
                variant="secondary"
                onPress={() => {
                  uploadFace().catch(() =>
                    setErrors(current => ({
                      ...current,
                      faceImage: 'Could not open the photo library.',
                    })),
                  );
                }}
                disabled={submitting}
                style={styles.faceAction}
              />
            </View>
            {errors.faceImage ? <Text style={themed.error}>{errors.faceImage}</Text> : null}
          </AppCard>

          <Pressable
            accessibilityRole="checkbox"
            accessibilityState={{ checked: values.consentGiven }}
            accessibilityLabel="Visitor consents to face capture"
            onPress={() => {
              setValues(current => ({ ...current, consentGiven: !current.consentGiven }));
              setErrors(current => ({ ...current, consentGiven: undefined }));
            }}
            style={styles.consentRow}
          >
            <View
              style={[
                styles.checkbox,
                values.consentGiven ? themed.checkboxOn : themed.checkbox,
              ]}
            >
              {values.consentGiven ? <Text style={themed.tick}>✓</Text> : null}
            </View>
            <Text style={[themed.body, styles.consentText]}>
              The visitor agrees to have their face captured and used only to check them in
              and out of this organization. They can ask for it to be removed at any time.
            </Text>
          </Pressable>
          {errors.consentGiven ? <Text style={themed.error}>{errors.consentGiven}</Text> : null}

          <AppButton
            title="Register visitor"
            onPress={submit}
            loading={submitting}
            disabled={submitting}
          />
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  cameraHost: { flex: 1 },
  cancelCapture: { margin: spacing.lg },
  form: { gap: spacing.md, marginTop: spacing.lg, paddingBottom: spacing.xl },
  banner: { borderWidth: 1, borderRadius: 8, padding: spacing.md },
  faceCard: { gap: spacing.sm },
  preview: {
    width: 160,
    height: 200,
    borderRadius: 12,
    borderWidth: 1,
    alignSelf: 'center',
  },
  faceActions: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  faceAction: { flexGrow: 1, minWidth: 140 },
  consentRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  consentText: { flex: 1 },
});
